require('smoothscroll-polyfill').polyfill();
import { create, removeChildren } from '../../utils/dom';
import { onTouchMove } from '../../utils/win';
import createSportsSelectionView from '../sport-selection-view/createSportSelectionView';
import {
    CSS_CLASS_BLOCK_NAME,
    CSS_HIDDEN_CSS_MODIFIER,
    HEADER_CSS_CLASS_BLOCK_NAME,
    FOOTER_CSS_CLASS_BLOCK_NAME,
    TITLE_CSS_CLASS_BLOCK_NAME,
    SELECTIONS_CSS_CLASS_BLOCK_NAME,
    SELECTION_CSS_CLASS_BLOCK_NAME,
    TITLE_TEXT,
    NAVIGATION_CSS_CLASS_BLOCK_NAME,
    NAVIGATION_RIGHT_CSS_MODIFIER,
    NAVIGATION_LEFT_CSS_MODIFIER,
    NAVIGATION_DISABLED_CSS_MODIFIER,
    SELECTION_VIEW_LEFT_MARGIN,
    SELECTION_VIEW_SHOWN_EVENT,
    SELECTION_VIEWS_SHOWN_EVENT,
    SELECTIONS_RIGHT_PADDING_CLASS_BLOCK_NAME,
    SLIDE_ANIMATION_DELAY_IN_MILLI_SECONDS,
    RECOMMENDED_SELECTION_CSS_ATTR,
    RECOMMENDED_SELECTION_GTM_ATTR,
    RECOMMENDED_SELECTION_GTM_ADD_BET,
    RECOMMENDED_SELECTION_GTM_SWIPE,
    NUMBER_OF_BETS_CSS_CLASS_BLOCK_NAME,
    TITLE_TEXT_DEFAULT,
} from './constants';
import './recommendations.scss';
import { createEventEmitter } from '../../utils/event';
import {
    isShowNumberOfBetsEnabled,
    numberOfBetsThreshold,
} from '../../utils/recommendations';
import I18n from './../../utils/i18n';
import {
    getIsMobileWrapperFlag,
} from '~shared/utils/user';

/**
 * Creates a recommendations view
 * @param {Boolean} gtmReceipt Optional boolean whether user is in bet receipt or betslip
 * @param {Array} [selections=[]] Optional selections array
 * @return {RecommendationsView} The recommendations view
 */
export default function createRecommendationsView(gtmReceipt = false, { selections = []} = {}) {
    const recommendationSource = gtmReceipt ? 'bet-receipt' : 'betslip';
    /**
     * The recommendations dom element
     * @type {Element}
     */
    const el = create('div', CSS_CLASS_BLOCK_NAME);

    /**
     * An event emmitter for the recommendations view
     * @type {EventEmitter}
     */
    const ev = createEventEmitter();

    /**
     * The left navigation dom element
     * @type {Element}
     */
    const navigateLeft = create(
        'span',
        `${NAVIGATION_CSS_CLASS_BLOCK_NAME} ${NAVIGATION_LEFT_CSS_MODIFIER}`
    );

    /**
     * The right navigation dom element
     * @type {Element}
     */
    const navigateRight = create(
        'span',
        `${NAVIGATION_CSS_CLASS_BLOCK_NAME} ${NAVIGATION_RIGHT_CSS_MODIFIER}`
    );

    navigateRight.setAttribute(RECOMMENDED_SELECTION_GTM_ATTR, `${recommendationSource};${RECOMMENDED_SELECTION_GTM_SWIPE}`);
    navigateLeft.setAttribute(RECOMMENDED_SELECTION_GTM_ATTR, `${recommendationSource};${RECOMMENDED_SELECTION_GTM_SWIPE}`);

    /**
     * The recommendations header dom element
     * @type {Element}
     */
    const header = create('div', HEADER_CSS_CLASS_BLOCK_NAME);

    /**
     * The recommendation footer dom element
     * @type {Element}
     */
    const footer = create('div', FOOTER_CSS_CLASS_BLOCK_NAME);

    /**
     * The recommendations title dom element
     * @type {Element}
     */
    const title = create('div', TITLE_CSS_CLASS_BLOCK_NAME, TITLE_TEXT_DEFAULT);

    /**
     * The recommendations header dom element
     * @type {Element}
     */
    const selectionsContainer = create('div', SELECTIONS_CSS_CLASS_BLOCK_NAME);

    /**
     * The selections right padding dom element
     * @type {Element}
     */
    const selectionsRightPadding = create('div', SELECTIONS_RIGHT_PADDING_CLASS_BLOCK_NAME);

    /**
     * Map of sport selection views
     * @type {Map}
     */
    const sportSelectionViews = new Map();

    /**
     * The selection view at the head position
     * @type {SportSelectionView}
     */
    let headSportSelectionView = null;

    /**
     * The selection view currently in view
     * @type {SportSelectionView}
     */
    let currentSportSelectionViewShown = null;

    /**
     * Sets the recommended selections
     * @param {Array} selections The list of recommended selections
     * @return {void}
     */
    function setSelections(selections) {
        headSportSelectionView = null;
        currentSportSelectionViewShown = null;

        hide();

        unconnectSelectionViewUpdates();

        setTimeout(
            () => renderSelections(selections),
            SLIDE_ANIMATION_DELAY_IN_MILLI_SECONDS
        );

    }

    /**
     * Renders selections
     * @param {Array} selections The selections to render
     * @return {void}
     */
    function renderSelections(selections) {
        removeChildren(selectionsContainer);

        sportSelectionViews.clear();

        selections.map(createSportsSelectionView)
            .reduce((prevSelectionView, sportSelectionView) => {
                if (prevSelectionView === null) {
                    headSportSelectionView = sportSelectionView;
                    currentSportSelectionViewShown = sportSelectionView;
                    sportSelectionView.prev = null;
                } else {
                    sportSelectionView.prev = prevSelectionView;
                    prevSelectionView.next = sportSelectionView;
                }

                sportSelectionView.el.appendChild(sportSelectionView.selectionView.el);
                sportSelectionView.el.classList.add(SELECTION_CSS_CLASS_BLOCK_NAME);
                sportSelectionView.selectionView.el.setAttribute(RECOMMENDED_SELECTION_CSS_ATTR, '');
                sportSelectionView.selectionView.el.setAttribute(RECOMMENDED_SELECTION_GTM_ATTR, `${recommendationSource};${RECOMMENDED_SELECTION_GTM_ADD_BET}`);
                selectionsContainer.appendChild(sportSelectionView.el);
                selectionsContainer.setAttribute(RECOMMENDED_SELECTION_GTM_ATTR, `${recommendationSource};${RECOMMENDED_SELECTION_GTM_SWIPE}`);
                sportSelectionView.next = null;

                sportSelectionViews.set(String(sportSelectionView.getId()), sportSelectionView);

                return sportSelectionView;
            }, headSportSelectionView);

        selectionsContainer.appendChild(selectionsRightPadding);

        ev.emit(SELECTION_VIEWS_SHOWN_EVENT, { selections });
    }

    /**
     * Scrolls the recommended selections to the left
     * @return {void}
     */
    function showPrevSelectionView() {
        if (hasPrevSelectionView()) {
            showSelectionView(currentSportSelectionViewShown.prev);
        }
    }

    /**
     * Scrolls the recommended selections to the right
     * @return {void}
     */
    function showNextSelectionView() {
        if (hasNextSelectionView()) {
            showSelectionView(currentSportSelectionViewShown.next);
        }
    }

    /**
     * Shows a selection view
     * @param {SportSelectionView} [view=null] The sport selection view
     * @return {void}
     */
    function showSelectionView(view = null) {
        if (view === null) {
            return;
        }

        selectionsContainer.scroll({
            left: view.el.offsetLeft - SELECTION_VIEW_LEFT_MARGIN,
            behavior: 'smooth',
        });

        currentSportSelectionViewShown = view;

        handleNumberOfBets();
        ev.emit(SELECTION_VIEW_SHOWN_EVENT, { view });
    }

    /**
     * Hides the recommendations view
     * @return {void}
     */
    function hide() {
        el.classList.add(CSS_HIDDEN_CSS_MODIFIER);
    }

    /**
     * Hides the recommendations view
     * @return {void}
     */
    function show() {
        el.classList.remove(CSS_HIDDEN_CSS_MODIFIER);
    }

    /**
     * Checks if there are selection views
     * @return {Boolean} Returns true if there are selection views
     */
    function isEmpty() {
        return headSportSelectionView === null;
    }

    /**
     * Checks if the recommendations view is hidden
     * @return {Boolean} Returns true if hidden
     */
    function isHidden() {
        return el.classList.contains(CSS_HIDDEN_CSS_MODIFIER);
    }

    /**
     * Checks if there is a previous selection view
     * @return {Boolean} Returns true if there is a previous selection
     */
    function hasPrevSelectionView() {
        return currentSportSelectionViewShown.prev !== null;
    }

    /**
     * Checks if there is a next selection view
     * @return {Boolean} Returns true if there is a previous selection
     */
    function hasNextSelectionView() {
        return currentSportSelectionViewShown.next !== null;
    }

    /**
     * Handles selection view changed event
     * @param {SportSelectionView} sportSelectionView The sport selection view
     * @return {void}
     */
    function sportSelectionViewChanged() {
        if (isEmpty()) {
            hide();

            return;
        }

        show();
        handleNavigationButtons();
    }

    /**
     * Handles navigation buttons
     * @return {void}
     */
    function handleNavigationButtons() {
        if (hasNextSelectionView()) {
            navigateRight.classList.remove(NAVIGATION_DISABLED_CSS_MODIFIER);
        } else {
            navigateRight.classList.add(NAVIGATION_DISABLED_CSS_MODIFIER);
        }

        if (hasPrevSelectionView()) {
            navigateLeft.classList.remove(NAVIGATION_DISABLED_CSS_MODIFIER);
        } else {
            navigateLeft.classList.add(NAVIGATION_DISABLED_CSS_MODIFIER);
        }
    }

    /**
     * Checks number of bets switch and number of bets threshold
     * @return {void}
     */
    function handleNumberOfBets() {
        const one = 1;
        const currentSelectionNumberOfBets = currentSportSelectionViewShown.getNumberOfBets();
        if (
            isShowNumberOfBetsEnabled() &&
            currentSelectionNumberOfBets >= numberOfBetsThreshold() &&
            currentSelectionNumberOfBets > one
        ){
            showNumberOfBetsRecommendationTitle();
        } else {
            showDefaultRecommendationTitle();
        }
    }

    /**
     * Shows number of bets recommendation selection
     * @return {void}
     */
    function showNumberOfBetsRecommendationTitle() {
        const countOfBets = currentSportSelectionViewShown.getNumberOfBets();
        title.innerHTML = I18n.t(
            TITLE_TEXT,
            { 'count': countOfBets, 'class': NUMBER_OF_BETS_CSS_CLASS_BLOCK_NAME }
        );
    }

    /**
     * Shows default recommendation title
     * @return {void}
     */
    function showDefaultRecommendationTitle() {
        title.textContent = I18n.t(TITLE_TEXT_DEFAULT);
    }

    /**
     * Checks if the selection id is in the recommended selection view
     * @param {Number|String} id The selection id
     * @return {Boolean} If the selection exists or not
     */
    function hasSelection(id) {
        return sportSelectionViews.has(String(id));
    }

    /**
     * Removes all sportselection views
     * @return {void}
     */
    function clear() {
        setSelections([]);
    }

    /**
     * Unsubscribe from selection view updates
     * @return {void}
     */
    function unconnectSelectionViewUpdates() {
        sportSelectionViews.forEach(view => view.unconnect());
    }

    /**
     * Subscribe to selection updates for current selection views
     * @return {void}
     */
    function connectSelectionViewUpdates() {
        sportSelectionViews.forEach(view => view.connect());
    }

    hide();

    ev.on(SELECTION_VIEW_SHOWN_EVENT, sportSelectionViewChanged);

    ev.on(SELECTION_VIEWS_SHOWN_EVENT, () => showSelectionView(headSportSelectionView));

    ev.on(SELECTION_VIEWS_SHOWN_EVENT, connectSelectionViewUpdates);

    navigateLeft.addEventListener('click', showPrevSelectionView);
    navigateRight.addEventListener('click', showNextSelectionView);

    onTouchMove({
        moveLeft: showNextSelectionView,
        moveRight: showPrevSelectionView,
        el: selectionsContainer,
    });

    setSelections(selections);

    header.appendChild(title);

    el.appendChild(header);

    el.appendChild(selectionsContainer);

    if (!getIsMobileWrapperFlag()) {

        footer.appendChild(navigateLeft);

        footer.appendChild(navigateRight);

        el.appendChild(footer);
    }


    return {
        el,
        ev,
        hide,
        show,
        clear,
        isEmpty,
        isHidden,
        hasSelection,
        setSelections,
    };
}
